import { useMemo } from 'react';

import { FavoriteCardData } from 'features/menu/pages/favorites-page/favorites-card-list/types';

import {
  IMinimalFavoriteFragment,
  useGetUserMinimalFavoritesQuery,
} from 'generated/graphql-gateway';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { routes } from 'utils/routing';

import { mapMinimalFavoriteToCardData } from './utils';

export const useMinimalFavorites = (enabled: boolean) => {
  const {
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();

  const { data, loading, refetch } = useGetUserMinimalFavoritesQuery({
    skip: !enabled,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const favorites = useMemo<FavoriteCardData[]>(() => {
    if (!enabled || isUnifiedMenuLoading) {
      return [];
    }

    return (
      data?.userMinimalFavorites?.favorites?.reduce<FavoriteCardData[]>((acc, favorite) => {
        if (!favorite) {
          return acc;
        }

        acc.push(
          mapMinimalFavoriteToCardData({
            favorite: favorite as IMinimalFavoriteFragment,
            menu: indexedMenu,
            indexedOffers,
            indexedRewards,
            posServiceMode,
            url: routes.menu,
          })
        );
        return acc;
      }, []) ?? []
    );
  }, [
    data?.userMinimalFavorites?.favorites,
    enabled,
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isUnifiedMenuLoading,
    posServiceMode,
  ]);

  return {
    favorites,
    loading: enabled ? loading || isUnifiedMenuLoading : false,
    refetch,
  };
};
